import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Flame, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import type { ListingsSectionProps, Listing } from '../types';
import { TABS } from '../data';
import ListingCard from './ListingCard';
import { useAds } from '../hooks/useAds';
import { useCategories } from '../hooks/useCategories';
import { adListItemToListing, tabToCategorySlug } from '../utils/adToListing';

export default function ListingsSection({
  activeTab,
  onTabChange,
  savedItems,
  onToggleSave,
}: ListingsSectionProps): JSX.Element {
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);

  const { ads, isLoading, isLoadingMore, error, hasMore, fetchAds, loadMore } = useAds();
  const { getCategoryBySlug } = useCategories();

  // Fetch whenever the active tab changes.
  useEffect(() => {
    const categorySlug = tabToCategorySlug(activeTab);
    const categoryId = categorySlug ? getCategoryBySlug(categorySlug)?.id : undefined;
    void fetchAds(categoryId ? { category_id: categoryId } : {});
    setVisibleCount(PAGE_SIZE);
  }, [activeTab, fetchAds, getCategoryBySlug]);

  // Map live ads into the legacy `Listing` shape consumed by `ListingCard`.
  const liveListings = useMemo<Listing[]>(() => ads.map(adListItemToListing), [ads]);

  const visibleListings = liveListings.slice(0, visibleCount);

  const handleTabChange = (tab: typeof activeTab): void => {
    setVisibleCount(PAGE_SIZE);
    onTabChange(tab);
  };

  const handleLoadMore = async (): Promise<void> => {
    // Prefer local pagination while we have cached items; only hit the API
    // once the user has exhausted the current page.
    if (visibleCount < liveListings.length) {
      setVisibleCount((c) => c + PAGE_SIZE);
      return;
    }
    if (hasMore && !isLoadingMore) {
      await loadMore();
      setVisibleCount((c) => c + PAGE_SIZE);
    }
  };

  const canShowMore =
    visibleCount < liveListings.length || (hasMore && !isLoadingMore);

  return (
    <section aria-labelledby="listings-heading" className="py-16 relative">
      {/* Background decoration */}
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-accent/20 to-primary-100/30 rounded-full blur-3xl -z-10" />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 text-accent text-sm font-semibold mb-2">
            <Flame className="w-4 h-4" />
            <span>Trending Now</span>
          </div>
          <h2
            id="listings-heading"
            className="section-title text-3xl sm:text-4xl font-bold text-dark-900 tracking-tight"
          >
            Latest <span className="gradient-text">Ads</span>
          </h2>
        </div>
        <a
          href="/listings"
          className="inline-flex items-center gap-2 bg-accent/10 text-accent hover:bg-accent/20 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all group fade-up"
          style={{ animationDelay: '0.1s' }}
        >
          See all
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>

      {/* Tabs */}
      <div
        role="group"
        aria-label="Filter listings by category"
        className="scroll-x flex gap-3 mb-8 pb-1 fade-up"
        style={{ animationDelay: '0.2s' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            aria-pressed={activeTab === tab}
            onClick={() => handleTabChange(tab)}
            className={`tab-btn px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
              activeTab === tab
                ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 scale-105'
                : 'bg-white text-dark-600 border border-dark-200 hover:border-primary-300 hover:text-primary-600 hover:shadow-md'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && !isLoading && (
        <div
          role="alert"
          className="mb-8 p-5 rounded-2xl bg-danger/5 border border-danger/20 flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-full bg-danger/10 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-dark-900">Couldn't load listings</p>
            <p className="text-xs text-dark-500 truncate">{error}</p>
          </div>
          <button
            onClick={() => {
              const slug = tabToCategorySlug(activeTab);
              const id = slug ? getCategoryBySlug(slug)?.id : undefined;
              void fetchAds(id ? { category_id: id } : {});
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white border border-dark-200 text-dark-700 text-xs font-bold hover:bg-dark-50 transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && liveListings.length === 0 && (
        <ul
          aria-busy="true"
          aria-label="Loading listings"
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5 list-none"
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <li
              key={i}
              className="bg-white rounded-2xl border border-dark-100 overflow-hidden shadow-sm"
            >
              <div className="h-52 bg-dark-100 animate-pulse" />
              <div className="p-4 space-y-3">
                <div className="h-4 w-1/3 bg-dark-100 rounded animate-pulse" />
                <div className="h-5 w-5/6 bg-dark-100 rounded animate-pulse" />
                <div className="h-5 w-2/3 bg-dark-100 rounded animate-pulse" />
                <div className="h-4 w-full bg-dark-100 rounded animate-pulse" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Empty state */}
      {!isLoading && !error && liveListings.length === 0 && (
        <div className="py-20 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-dark-100 mb-4">
            <Flame className="w-7 h-7 text-dark-400" />
          </div>
          <h3 className="text-lg font-bold text-dark-900">No listings yet</h3>
          <p className="text-sm text-dark-500 mt-1">
            Try a different category — new items are posted every day.
          </p>
        </div>
      )}

      {/* Cards grid */}
      {liveListings.length > 0 && (
        <>
          <ul
            id="listings-panel"
            role="list"
            className="listings-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-5 list-none"
          >
            {visibleListings.map((listing, i) => (
              <li
                key={listing.uuid ?? listing.id}
                className="fade-up h-full"
                style={{ animationDelay: `${0.3 + i * 0.05}s` }}
              >
                <ListingCard
                  listing={listing}
                  isSaved={savedItems.includes(listing.id)}
                  onToggleSave={onToggleSave}
                  animationDelay={i * 0.07}
                />
              </li>
            ))}
          </ul>

          {canShowMore && (
            <div className="flex justify-center mt-10">
              <button
                onClick={() => void handleLoadMore()}
                disabled={isLoadingMore}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-white border border-dark-200 text-dark-800 font-bold text-sm hover:border-primary-300 hover:text-primary-600 hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading…
                  </>
                ) : (
                  <>
                    Show more
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
